import client from 'client'
import type {
  ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationRequest,
  ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationResponse,
  ResendAssetFilesForPeriodAssetAssetIdFilesResendPostPathParams,
  ResendAssetFilesForPeriodAssetAssetIdFilesResendPostQueryParams,
  ResendAssetFilesForPeriodAssetAssetIdFilesResendPost422,
} from '../../types/ResendAssetFilesForPeriodAssetAssetIdFilesResendPost.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const resendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationKey = () => [{ url: '/asset/{asset_id}/files/resend' }] as const

export type ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationKey = ReturnType<typeof resendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationKey>

/**
 * @summary Resend Asset Files For Period
 * {@link /asset/:asset_id/files/resend}
 */
export async function resendAssetFilesForPeriodAssetAssetIdFilesResendPost(
  asset_id: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostPathParams['asset_id'],
  data: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationRequest,
  params?: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostQueryParams,
  config: Partial<RequestConfig<ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationResponse,
    ResponseErrorConfig<ResendAssetFilesForPeriodAssetAssetIdFilesResendPost422>,
    ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationRequest
  >({ method: 'POST', url: `/asset/${asset_id}/files/resend`, params, data, ...requestConfig })
  return res
}

/**
 * @summary Resend Asset Files For Period
 * {@link /asset/:asset_id/files/resend}
 */
export function useResendAssetFilesForPeriodAssetAssetIdFilesResendPost(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationResponse>,
      ResponseErrorConfig<ResendAssetFilesForPeriodAssetAssetIdFilesResendPost422>,
      {
        asset_id: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostPathParams['asset_id']
        data: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationRequest
        params?: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostQueryParams
      }
    >
    client?: Partial<RequestConfig<ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? resendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationKey()

  return useMutation<
    ResponseConfig<ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationResponse>,
    ResponseErrorConfig<ResendAssetFilesForPeriodAssetAssetIdFilesResendPost422>,
    {
      asset_id: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostPathParams['asset_id']
      data: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostMutationRequest
      params?: ResendAssetFilesForPeriodAssetAssetIdFilesResendPostQueryParams
    }
  >({
    mutationFn: async ({ asset_id, data, params }) => {
      return resendAssetFilesForPeriodAssetAssetIdFilesResendPost(asset_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}